import {BaseGameModeData, GameModeDataController} from '@colonist/shared-types'
import {RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {UIMRoomCardView} from './UIMRoomCardView'
import {ModeSettingEnum, TemporaryModeSettingEnum} from '@colonist/game-base-types'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {createDivIn, createSpanIn, createLinkIn, setElementInnerHTML, createImgIn, createDiv} from '../../../../shared/scripts/ui_utils/DomUtils'
import {TranslationArrayKeys, TranslationObject} from '@colonist/i18n'
import {clientI18n} from '../../../../shared/scripts/ClientI18n'
import {socket} from '../../../../shared/scripts/EntryShared.ts'
import {UIPopupController} from '../../../../shared/scripts/ui_popup/UIPopupController.ts'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables.ts'

export class UIMRoomModeController {
    private readonly container: HTMLDivElement
    private readonly cardList: {modeData: BaseGameModeData, element: UIMRoomCardView, isModeAccessible: boolean}[] = []

    isSelectedModeAccessible: boolean = true

    constructor(container: HTMLDivElement, roomState: RoomClientState) {
        this.container = container

        this.createMembershipCTA()
        this.createAllCards()
        this.update(roomState)
    }

    update(roomState: RoomClientState) {
        for(const card of this.cardList) {
            const isSelected = roomState.gameSetting.modeSetting == card.modeData.modeSetting
            card.element.setSelect(isSelected)
            if(isSelected) this.isSelectedModeAccessible = card.isModeAccessible
        }
    }

    private createMembershipCTA() {
        if(ClientUserState.userState?.items.hasMembership()) return

        const membershipCTAContainer = createDivIn(this.container, 'room_center_become_a_member mobile-room')
        createSpanIn(membershipCTAContainer, '', {key: 'strings:roomPage.getAccessToAllMapsAndExpansions'})
        createLinkIn(membershipCTAContainer, '/store#membership', {key: 'strings:homePage.becomeAMember'}, 'btn_general btn_general_no_margin btn_general_orange btn-general-become-a-member', '', true)
    }

    private createAllCards() {
        const allGameModeData = GameModeDataController.publicGameModeDataList
        for(const gameModeData of allGameModeData) {
            if(gameModeData.temporaryModeSetting == TemporaryModeSettingEnum.ServerStressTest) continue
            if(gameModeData.modeSetting == ModeSettingEnum.ColonistRush4P && !UIUtilsEjsVariables.colonistRushEnabled) continue
            const isCardLocked = !ClientUserState.userState?.items.hasAccessToPremiumMode(gameModeData)
            const gameModeName = UIMRoomModeController.getGameModeName(gameModeData)
            const clickAction = () => socket.roomClient.setGameMode(gameModeData.modeSetting)
            const displayInfoAction = () => UIMRoomModeController.displayGameModeInfo(gameModeData)

            const card = new UIMRoomCardView(this.container, gameModeName, gameModeData.icon, isCardLocked, false, clickAction, displayInfoAction)
            if(UIMRoomModeController.shouldShowAlphaBanner(gameModeData.modeSetting)) card.createBanner({key: 'strings:roomPage.options.gameMode.alpha'})
            this.cardList.push({modeData: gameModeData, element: card, isModeAccessible: !isCardLocked})
        }
    }

    private static displayGameModeInfo(gameModeData: BaseGameModeData) {
        const container = createDiv('room-map-preview-grid vertical')
        createImgIn(container, gameModeData.icon, 'room-item-popup-image')
        const gameModeInfoBody = UIMRoomModeController.getGameModeInfoBody(gameModeData)
        const text = UIMRoomModeController.createSingleMapInfo(clientI18n.tArray(gameModeInfoBody))
        const translatedMessage: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {
            value: text,
            interpolation: {
                escapeValue: false,
            },
        }}
        const parentDiv = createDivIn(container, 'room-item-popup-text mobile')
        setElementInnerHTML(parentDiv, translatedMessage)
        const popup = UIPopupController.createSimplePopup(gameModeData.name, {key: 'strings:colonistCommon.utils.emptyString'})
        if(popup === undefined) return

        popup.body.appendChild(container)
    }

    private static createSingleMapInfo(body: string[]): string {
        let text = '<ul>'
        for(const element of body) {
            text += '<li>' + element
            text += '</li>'
        }
        text += '</ul>'
        return text
    }

    private static getGameModeInfoBody(gameModeData: BaseGameModeData): TranslationArrayKeys {
        switch(gameModeData.modeSetting) {
            case ModeSettingEnum.Classic4P: return 'strings:colonistCommon.gameModes.classic4P.body'
            case ModeSettingEnum.TutorialClassic4P:
            case ModeSettingEnum.BeginnerModeClassic4P: return 'strings:colonistCommon.gameModes.classicTutorial4P.body'
            case ModeSettingEnum.Classic56P: return 'strings:colonistCommon.gameModes.classic56P.body'
            case ModeSettingEnum.Classic78P: return 'strings:colonistCommon.gameModes.classic78P.body'
            case ModeSettingEnum.Seafarers4P: return 'strings:colonistCommon.gameModes.seafarers4P.body'
            case ModeSettingEnum.Seafarers56P: return 'strings:colonistCommon.gameModes.seafarers56P.body'
            case ModeSettingEnum.CitiesAndKnights4P: return 'strings:colonistCommon.gameModes.citiesAndKnights4P.body'
            case ModeSettingEnum.CitiesAndKnights56P: return 'strings:colonistCommon.gameModes.citiesAndKnights56P.body'
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights4P.body'
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights56P.body'
            case ModeSettingEnum.ServerStressTestClassic4P: return 'strings:colonistCommon.gameModes.serverStressTestClassic4P.body'
            case ModeSettingEnum.ColonistRush4P: return 'strings:colonistCommon.gameModes.colonistRush.body'
        }
    }

    private static getGameModeName(gameModeData: BaseGameModeData): TranslationObject {
        switch(gameModeData.modeSetting) {
            case ModeSettingEnum.Classic56P: return {key: 'strings:colonistCommon.gameModes.classic56P.abbreviation'}
            case ModeSettingEnum.Classic78P: return {key: 'strings:colonistCommon.gameModes.classic78P.abbreviation'}
            case ModeSettingEnum.Seafarers56P: return {key: 'strings:colonistCommon.gameModes.seafarers56P.abbreviation'}
            case ModeSettingEnum.CitiesAndKnights56P: return {key: 'strings:colonistCommon.gameModes.citiesAndKnights56P.abbreviation'}
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return {key: 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights4P.abbreviation'}
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return {key: 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights56P.abbreviation'}
            default: return gameModeData.name
        }
    }

    private static shouldShowAlphaBanner(gameMode: ModeSettingEnum): boolean {
        switch(gameMode) {
            case ModeSettingEnum.ColonistRush4P:
                return true
            default:
                return false
        }
    }
}
